// @flow
import React, { useEffect } from 'react'
import styled from 'styled-components'
import PrimaryButton from '../controls/PrimaryButton'
import ExposedSelect from '../controls/ExposedSelect'
import type { BriefData, PaymentMethod } from './BriefContainer'
import ErrorText from '../controls/ErrorText'
import _ from 'lodash'
import MediaLoaderContainer from '../AdEdit/MediaLoaderContainer'
import ImagePreviewContainer from '../ImagePreviewContainer'
import BriefBudgetSelect, { resultProjection, translateBudgetOption } from './BriefBudgetSelect'
import Tooltip from '../Tooltip'
import TextWithNewlines from '../TextWithNewlines'
import type { StyledComponent } from 'styled-components'
import NativeSelect from '../controls/NativeSelect'
import LineInput from '../AdEdit/AdInputs/LineInput'

const Block = styled.div`
  display: flex;
  padding: 10px 0 20px;
  @media (max-width: 600px) {
    display: block;
  }
`
const SummaryBlock = styled.div`
  display: flex;
  padding: 10px 0;
  border-top: solid 1px #ccc;
  @media (max-width: 600px) {
    display: block;
  }
  ${({ isLastBlock }) => (isLastBlock ? 'border-bottom: solid 1px #ccc;' : '')}
`
const Question = styled.div`
  width: 40%;
  padding-right: 8px;
  @media (max-width: 600px) {
    width: 100%;
  }
`
const Answer = styled.div`
  width: 60%;
  @media (max-width: 600px) {
    width: 100%;
  }
`
const SummaryQuestion = styled.div`
  width: 40%;
  padding-right: 8px;
  @media (max-width: 600px) {
    padding-right: 0;
    width: 100%;
  }
`
const SummaryAnswer = styled.div`
  width: 60%;
  @media (max-width: 600px) {
    width: 100%;
  }
`
const Title = styled.div`
  font-size: 16px;
  font-weight: bold;
  padding-bottom: 8px;
`
const LightTitle = styled.div`
  font-size: 16px;
  padding-bottom: 8px;
  @media (max-width: 600px) {
    font-weight: bold;
  }
`
const Description = styled.div`
  font-size: 14px;
  padding-bottom: 8px;
`
const Header = styled.div`
  font-size: 28px;
  font-weight: bold;
  padding 48px 0 20px;
`
const Spacer = styled.div`
  height: 20px;
`
const BriefDescription = styled.div`
  font-size: 16px;
  padding-bottom: 36px;
`
const ImagePreviewWrapper = styled.div`
  padding: 2px;
  display: inline-block;
`
const Wrapper: StyledComponent<Object, any, any> = styled.div`
  width: 100%;
  padding: 0 20px;
  max-width: 980px;
  margin: 0 auto;
  @media (max-width: 600px) {
    padding: 0 10px;
  }
`

const t = {
  send: 'Отправить'
}

const adDurationOptions = [
  { id: 15, name: '15 дней' },
  { id: 21, name: '21 день' },
  { id: 30, name: '30 дней' },
  { id: 60, name: '60 дней' }
]

type Props = {|
  data: BriefData,
  errors: Object,
  onChange: ($Shape<BriefData>) => void,
  onSend: () => void,
  onClear: () => void,
  showSummary: boolean,
  commonError: string,
  imageStoreIds: Array<string>,
  onImagesLoad: (Array<string>) => void,
  imagesError: string,
  onRemoveImage: (string) => void,
  paymentError: string,
  paymentMethod: PaymentMethod,
  onPaymentMethodChange: (PaymentMethod) => void,
  onPerformPayment: () => void
|}

function SingleError(props: {| text: ?string |}) {
  return props.text ? <ErrorText errors={[props.text]} /> : null
}

export default function Brief(props: Props) {
  useEffect(() => {
    setTimeout(() => {
      window.dispatchEvent(new window.Event('resize'))
    }, 500) // HACK: For auto-size text areas
  })
  return <Wrapper>{props.showSummary ? renderSummary(props) : renderForm(props)}</Wrapper>
}

function currentAdDurationOption(currentAdDuration: string) {
  return (
    (_.find(adDurationOptions, (option) => option.id === parseInt(currentAdDuration)) || {}).name ||
    ''
  )
}

function renderSummary(props: Props) {
  const imagesCount = ((props.data: Object)['image_ids'] || props.imageStoreIds).length
  return (
    <div>
      <Header>Всё готово!</Header>
      <div>Ожидаемый результат:</div>
      <div>
        {_.map(resultProjection(props.data.budget, props.data.ad_object_type), (line, index) => (
          <div key={index}>{line}</div>
        ))}
      </div>
      <Spacer />
      <div>{`Будем показывать рекламу ${currentAdDurationOption(
        props.data.ad_duration
      )} с бюджетом ${parseInt(props.data.budget)} рублей в день`}</div>
      <div>{`Сумма к оплате ${parseInt(props.data.ad_duration) *
        parseInt(props.data.budget)} рублей`}</div>
      <Spacer />
      <div>
        <NativeSelect
          width={300}
          value={props.paymentMethod}
          options={[
            { id: 'card', name: 'Банковская карта' },
            { id: 'robokassa', name: 'Робокасса' },
            { id: 'bank', name: 'Банковский перевод (для юр.лиц)' },
            { id: 'qiwi', name: 'Qiwi' },
            { id: 'yandex', name: 'Яндекс.Деньги' },
            { id: 'webmoney', name: 'Webmoney' }
          ]}
          onChange={props.onPaymentMethodChange}
        />
      </div>
      <Spacer />
      <SingleError text={props.paymentError} />
      <PrimaryButton small onClick={() => props.onPerformPayment()}>
        Оплатить
      </PrimaryButton>
      <Header>Ваш бриф</Header>
      <Spacer />
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Объект рекламы</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.ad_object} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Цель рекламы</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.ad_target} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Ваши клиенты</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.ad_audience} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Ваши преимущества</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.product_best_sides} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Популярные товары</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.best_products} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Графические материалы</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines
            value={`${imagesCount} файл${_.includes([2, 3, 4], imagesCount) ? 'а' : ''}${
              imagesCount >= 5 ? 'ов' : ''
            }`}
          />
        </SummaryAnswer>
      </SummaryBlock>
      {props.data.ad_object_type === 'site' && (
        <SummaryBlock>
          <SummaryQuestion>
            <LightTitle>Посещаемость сайта</LightTitle>
          </SummaryQuestion>
          <SummaryAnswer>
            <TextWithNewlines value={props.data.visits_per_month} />
          </SummaryAnswer>
        </SummaryBlock>
      )}
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>База клиентов</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.crm_existence ? 'Есть' : 'Отсутствует'} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Адреса</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.addresses} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Примечание</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={props.data.comment} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock>
        <SummaryQuestion>
          <LightTitle>Бюджет</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={translateBudgetOption(props.data.budget)} />
        </SummaryAnswer>
      </SummaryBlock>
      <SummaryBlock isLastBlock>
        <SummaryQuestion>
          <LightTitle>Длительность</LightTitle>
        </SummaryQuestion>
        <SummaryAnswer>
          <TextWithNewlines value={currentAdDurationOption(props.data.ad_duration)} />
        </SummaryAnswer>
      </SummaryBlock>
      <PrimaryButton small onClick={() => props.onClear()}>
        Отправить другой бриф
      </PrimaryButton>
    </div>
  )
}

function renderForm(props: Props) {
  return (
    <div>
      <Header>Настройка рекламы</Header>
      <BriefDescription>
        Ответьте на несколько простых вопросов - это займет 5 минут вашего времени.
        <br />
        OneRetarget выберет оптимальные рекламные сети для запуска, максимально эффективно настроит
        рекламные кампании и запустит привлечение клиентов.
        <br />
        <br />
        Вы получите уведомление о запуске и сможете смотреть статистику в онлайн режиме.
      </BriefDescription>
      <SingleError text={props.commonError} />
      <Block>
        <Question>
          <Title>Что рекламируем?</Title>
          <Description>
            Укажите ссылку на сайт или ваше сообщество ВКонтакте или на профиль Instagram
          </Description>
        </Question>
        <Answer>
          <LineInput
            inputProps={{ placeholder: 'https://your-landing.com' }}
            value={props.data.ad_object}
            onChange={(value) => {
              props.onChange({
                ad_object: value,
                ad_object_type: adObjectType(value)
              })
            }}
          />
          <SingleError text={props.errors.ad_object} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Опишите цель рекламы максимально конкретно</Title>
        </Question>
        <Answer>
          <LineInput
            inputProps={{ placeholder: 'Максимум 5 слов' }}
            value={props.data.ad_target}
            onChange={(value) => props.onChange({ ad_target: value })}
          />
          <SingleError text={props.errors.ad_target} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Опишите тех кто сейчас у вас покупает</Title>
          <Description>Пол, возраст, портрет или интересы вашего покупателя</Description>
        </Question>
        <Answer>
          <LineInput
            multiline
            inputProps={{ placeholder: 'Например, женщины 30-50 лет ...', minRows: 2 }}
            value={props.data.ad_audience}
            onChange={(value) => props.onChange({ ad_audience: value })}
          />
          <SingleError text={props.errors.ad_audience} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Почему клиент должен купить именно у вас и именно сейчас?</Title>
          <Description>
            Что выделяет ваш товар (или услуги) на фоне конкурентов? Скидки, акции, гарантии
            особенности доставки, чрезвычайно внимательный персонал, ограниченное количество самого
            товара, что-нибудь еще?
          </Description>
        </Question>
        <Answer>
          <LineInput
            multiline
            inputProps={{ placeholder: 'Ваши преимущества', minRows: 2 }}
            value={props.data.product_best_sides}
            onChange={(value) => props.onChange({ product_best_sides: value })}
          />
          <SingleError text={props.errors.product_best_sides} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Популярные товары</Title>
          <Description>
            Перечислите от 1 до 5 ваших товаров и услуг, которые покупают лучше всего. Укажите их
            стоимость
          </Description>
        </Question>
        <Answer>
          <LineInput
            multiline
            inputProps={{ placeholder: 'Список товаров', minRows: 2 }}
            value={props.data.best_products}
            onChange={(value) => props.onChange({ best_products: value })}
          />
          <SingleError text={props.errors.best_products} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Предоставьте графические материалы по продукту</Title>
          <Description>
            Фото продукта, фото процесса производства, фото людей с продуктом, тематические
            картинки, рисунки и т.п.
            <br />
            <br />
            не более 20 изображений, каждое не более 10 мегабайт
          </Description>
        </Question>
        <Answer>
          {_.map(props.imageStoreIds, (storeId) => (
            <ImagePreviewWrapper key={storeId}>
              <Tooltip
                content={
                  <PrimaryButton small onClick={() => props.onRemoveImage(storeId)}>
                    Удалить
                  </PrimaryButton>
                }
                isInteractive
                position="top"
                hideArrow
              >
                <ImagePreviewContainer width={80} storeId={storeId} />
              </Tooltip>
            </ImagePreviewWrapper>
          ))}
          <div>
            <MediaLoaderContainer
              mediaType="image"
              storeId={null}
              onLoad={(storeIds) => props.onImagesLoad(storeIds)}
              withoutMediaPreview
              multiple
              maxImageSize={1.05e6}
              title="Выберите файлы..."
              titleFontSize={14}
            />
          </div>
          <SingleError text={props.imagesError} />
        </Answer>
      </Block>
      {props.data.ad_object_type === 'site' && (
        <Block>
          <Question>
            <Title>Примерная месячная посещаемость вашего сайта</Title>
          </Question>
          <Answer>
            <LineInput
              inputProps={{ placeholder: 'Количество визитов за последние 30 дней' }}
              value={props.data.visits_per_month}
              onChange={(value) => props.onChange({ visits_per_month: value })}
            />
            <SingleError text={props.errors.visits_per_month} />
          </Answer>
        </Block>
      )}
      <Block>
        <Question>
          <Title>У вас есть база телефонов или email ваших клиентов?</Title>
        </Question>
        <Answer>
          <ExposedSelect
            value={props.data.crm_existence}
            onChange={(value) => props.onChange({ crm_existence: value })}
            options={[
              { id: 'yes', name: 'Да' },
              { id: 'no', name: 'Нет' }
            ]}
          />
          <SingleError text={props.errors.crm_existence} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Локальный бизнес?</Title>
          <Description>Укажите адреса, куда приходят клиенты</Description>
        </Question>
        <Answer>
          <LineInput
            multiline
            inputProps={{ placeholder: 'Адреса', minRows: 2 }}
            value={props.data.addresses}
            onChange={(value) => props.onChange({ addresses: value })}
          />
          <SingleError text={props.errors.addresses} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Примечание</Title>
          <Description>Дополнительная информация в свободной форме</Description>
        </Question>
        <Answer>
          <LineInput
            multiline
            inputProps={{ placeholder: 'Комментарий', minRows: 2 }}
            value={props.data.comment}
            onChange={(value) => props.onChange({ comment: value })}
          />
          <SingleError text={props.errors.comment} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Бюджет на рекламу</Title>
          <Description>
            {props.data.ad_object_type === 'site'
              ? 'В коммерческих тематиках стоимость перехода составит от 10 до 25 рублей. Стоимость конверсии (целевого действия) от 200 до 2000 рублей.'
              : 'Стоимость подписчика в коммерческих тематиках в среднем будет составлять от 50 до 100 рублей.'}
          </Description>
        </Question>
        <Answer>
          <BriefBudgetSelect
            adObjectType={props.data.ad_object_type}
            value={props.data.budget}
            onChange={props.onChange}
          />
          <SingleError text={props.errors.budget} />
        </Answer>
      </Block>
      <Block>
        <Question>
          <Title>Длительность рекламы</Title>
          <Description>
            Выберите количество дней, на которое вы хотите оплатить работу рекламы
          </Description>
        </Question>
        <Answer>
          <ExposedSelect
            value={props.data.ad_duration}
            onChange={(value) => props.onChange({ ad_duration: value })}
            options={adDurationOptions}
          />
          <SingleError text={props.errors.ad_duration} />
        </Answer>
      </Block>
      <Spacer />
      <PrimaryButton onClick={() => props.onSend()}>{t.send}</PrimaryButton>
    </div>
  )
}

function adObjectType(url: string): 'site' | 'vk' | 'instagram' {
  if (url.includes('vk.com')) {
    return 'vk'
  } else if (url.includes('instagram.com')) {
    return 'instagram'
  } else {
    return 'site'
  }
}
